/**
 * HOS Context Provider
 * Centralized state management for Hospice Operating System
 */

import { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { canAccessFeature, ROLE_HIERARCHY } from "@/components/ProtectedRoute";

const API_URL = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || "";

const HOSContext = createContext(null);

// Auth headers helper
const getAuthHeaders = () => {
  const token = localStorage.getItem("hospice_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Event type configuration
export const EVENT_CONFIG = {
  visit_note: { color: "bg-blue-500", label: "Visit Note", icon: "file-text" },
  skilled_nurse: { color: "bg-blue-500", label: "Skilled Nurse", icon: "file-text" },
  documentation: { color: "bg-blue-500", label: "Documentation", icon: "file-text" },
  physician_order: { color: "bg-violet-500", label: "Physician Order", icon: "stethoscope" },
  medication: { color: "bg-violet-500", label: "Medication", icon: "pill" },
  chaplain_visit: { color: "bg-purple-500", label: "Chaplain", icon: "heart" },
  social_work: { color: "bg-pink-500", label: "Social Work", icon: "users" },
  volunteer_log: { color: "bg-orange-500", label: "Volunteer", icon: "heart" },
  family_message: { color: "bg-cyan-500", label: "Family", icon: "message-square" },
  phone_call: { color: "bg-gray-500", label: "Phone Call", icon: "phone" },
  idg_discussion: { color: "bg-teal-500", label: "IDG", icon: "users" },
  incident: { color: "bg-red-500", label: "Incident", icon: "alert-triangle" },
  dme_order: { color: "bg-slate-500", label: "DME", icon: "package" },
  supply_order: { color: "bg-slate-500", label: "Supply", icon: "package" },
  hope_assessment: { color: "bg-amber-500", label: "HOPE", icon: "heart" },
  vital_signs: { color: "bg-green-500", label: "Vitals", icon: "activity" },
  symptom_log: { color: "bg-yellow-500", label: "Symptoms", icon: "activity" },
  bereavement_note: { color: "bg-indigo-500", label: "Bereavement", icon: "heart" },
  unified_visit: { color: "bg-teal-600", label: "Unified Visit", icon: "file-text" }
};

export function HOSProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { patientId } = useParams();
  const { user } = useAuth();
  
  // Get user role for access control
  const userRole = user?.role || user?.user_type || "staff";
  const isAdmin = ROLE_HIERARCHY.admin.includes(userRole.toLowerCase());
  
  // Core patient state
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientContext, setPatientContext] = useState(null);
  const [patientList, setPatientList] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Overlay state
  const [activeOverlay, setActiveOverlay] = useState(null);
  const [overlayData, setOverlayData] = useState(null);
  
  // Command palette state
  const [commandOpen, setCommandOpen] = useState(false);
  const [commandQuery, setCommandQuery] = useState("");
  const [commandResults, setCommandResults] = useState([]);
  const [contextualSuggestions, setContextualSuggestions] = useState([]);
  const [allCommands, setAllCommands] = useState([]);
  
  // Patient quick-switch
  const [quickSwitchOpen, setQuickSwitchOpen] = useState(false);
  
  // Voice dictation state
  const [isRecording, setIsRecording] = useState(false);
  const [transcription, setTranscription] = useState("");
  const [voiceSessionId, setVoiceSessionId] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  
  // Timeline filters
  const [timelineFilters, setTimelineFilters] = useState({
    event_types: null,
    search_text: ""
  });
  
  // Compliance state
  const [complianceResult, setComplianceResult] = useState(null);
  const [noteContent, setNoteContent] = useState("");

  // URL params handling
  const searchParams = new URLSearchParams(location.search);
  const urlOverlay = searchParams.get("overlay");

  // Handle URL overlay parameter
  useEffect(() => {
    if (urlOverlay && !activeOverlay) {
      setActiveOverlay(urlOverlay);
      toast.info(`Opening ${urlOverlay.replace(/_/g, " ")}`, {
        description: "Redirected from legacy route"
      });
    }
  }, [urlOverlay, activeOverlay]);

  // Fetch patient context
  const fetchPatientContext = useCallback(async (pid) => {
    if (!pid) return;
    
    try {
      const response = await axios.get(`${API_URL}/api/hos/context/${pid}`, {
        headers: getAuthHeaders()
      });
      setPatientContext(response.data);
      setSelectedPatient({
        id: pid,
        name: response.data.patient_name,
        status: response.data.status
      });
    } catch (err) {
      // Demo context fallback
      setPatientContext({
        patient_id: pid,
        patient_name: "John Smith",
        status: "active",
        available_actions: [
          { id: "smart_documentation", name: "Smart Documentation", priority: "high" },
          { id: "mar", name: "Medication Record", priority: "normal" },
          { id: "vital_signs", name: "Record Vitals", priority: "normal" }
        ],
        alerts: [
          { type: "compliance", message: "HOPE update due in 3 days", severity: "warning" },
          { type: "clinical", message: "Pain score increased - review medication", severity: "high" }
        ],
        quick_stats: {
          days_in_program: 125,
          benefit_period: "2nd",
          loc: "RHC",
          last_visit: "2 hours ago",
          next_visit: "Tomorrow 10:00 AM"
        }
      });
      setSelectedPatient({ id: pid, name: "John Smith", status: "active" });
    }
  }, []);

  // Fetch timeline
  const fetchTimeline = useCallback(async (pid) => {
    if (!pid) return;
    setLoading(true);
    
    try {
      const response = await axios.post(`${API_URL}/api/hos/timeline`, {
        patient_id: pid,
        event_types: timelineFilters.event_types,
        search_text: timelineFilters.search_text || null,
        limit: 50
      }, { headers: getAuthHeaders() });
      
      setTimeline(response.data.events || []);
    } catch (err) {
      console.error("Failed to fetch timeline:", err);
    } finally {
      setLoading(false);
    }
  }, [timelineFilters]);

  // Fetch patient list
  const fetchPatientList = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/patients`, {
        headers: getAuthHeaders(),
        params: { status: "active", limit: 50 }
      });
      const patients = response.data.patients || response.data || [];
      setPatientList(patients.map(p => ({
        id: p.id || p._id,
        name: p.name || `${p.first_name || ""} ${p.last_name || ""}`.trim(),
        mrn: p.mrn || p.medical_record_number,
        status: p.status || "active"
      })));
    } catch (err) {
      console.log("Using demo patient list");
    }
  }, []);

  // Fetch commands
  const fetchCommands = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/hos/command-palette/all`, {
        headers: getAuthHeaders()
      });
      // Backend already filters by role, but double-check on frontend
      const commands = response.data.commands || [];
      // Filter admin_only commands if not admin (snake_case from API)
      const filteredCommands = isAdmin ? commands : commands.filter(cmd => !cmd.admin_only);
      setAllCommands(filteredCommands);
    } catch (err) {
      // Demo commands fallback with role filtering
      setAllCommands(getDefaultCommands(isAdmin));
    }
  }, [isAdmin]);

  // Initialize data on mount and when user/role changes
  useEffect(() => {
    // Fetch commands - will use fallback if API fails
    fetchCommands();
  }, [fetchCommands]);
  
  useEffect(() => {
    fetchPatientList();
  }, [fetchPatientList]);

  useEffect(() => {
    const pid = patientId || "demo-patient";
    fetchPatientContext(pid);
    fetchTimeline(pid);
  }, [patientId, fetchPatientContext, fetchTimeline]);

  // Get recommended template
  const getRecommendedTemplate = useCallback(() => {
    if (!patientContext) return null;
    
    const stats = patientContext.quick_stats || {};
    const alerts = patientContext.alerts || [];
    
    const hasPainAlert = alerts.some(a => a.message?.toLowerCase().includes("pain"));
    const hasSymptomAlert = alerts.some(a => a.type === "clinical");
    
    if (hasPainAlert) {
      return { type: "pain_assessment", name: "Pain Assessment Visit", reason: "Elevated pain score detected" };
    }
    if (hasSymptomAlert) {
      return { type: "symptom_management", name: "Symptom Management Visit", reason: "Clinical alert triggered" };
    }
    if (stats.days_in_program && stats.days_in_program <= 7) {
      return { type: "admission_followup", name: "Admission Follow-up Visit", reason: "New admission (within 7 days)" };
    }
    if (stats.benefit_period === "2nd" || stats.benefit_period === "3rd") {
      return { type: "recertification", name: "Recertification Visit", reason: `Benefit Period: ${stats.benefit_period}` };
    }
    
    return { type: "routine", name: "Routine Visit Note", reason: "Standard care visit" };
  }, [patientContext]);

  // Open overlay
  const openOverlay = useCallback((overlayId, data = null) => {
    if (overlayId === "visit_note" || overlayId === "new_visit_note") {
      const recommended = getRecommendedTemplate();
      if (recommended) {
        setOverlayData({ ...data, recommendedTemplate: recommended });
        toast.info(`Suggested: ${recommended.name}`, {
          description: recommended.reason,
          duration: 4000
        });
      } else {
        setOverlayData(data);
      }
    } else {
      setOverlayData(data);
    }
    setActiveOverlay(overlayId);
    setCommandOpen(false);
  }, [getRecommendedTemplate]);

  // Close overlay
  const closeOverlay = useCallback(() => {
    setActiveOverlay(null);
    setOverlayData(null);
  }, []);

  // Execute command
  const executeCommand = useCallback((cmd) => {
    // Check if command is disabled
    if (cmd.disabled || cmd.comingSoon) {
      toast.info(`${cmd.name} - Coming Soon`, {
        description: cmd.description || "This feature is being enhanced",
        duration: 3000
      });
      setCommandOpen(false);
      setCommandQuery("");
      return;
    }
    
    if (cmd.action?.startsWith("overlay:")) {
      openOverlay(cmd.action.replace("overlay:", ""));
    } else if (cmd.action?.startsWith("mode:")) {
      openOverlay(cmd.action.replace("mode:", ""));
    } else if (cmd.action?.startsWith("navigate:")) {
      // Check if this is a navigation that should stay on home screen as an overlay
      const navPath = cmd.action.replace("navigate:", "");
      const overlayPaths = [
        "/staff-portal", "/family-portal", "/predictive-alerts", "/acuity-trends",
        "/patient-acuity", "/smart-scheduling", "/bereavement-support"
      ];
      
      // If it's a feature that can be shown as overlay, open it as overlay instead
      if (overlayPaths.includes(navPath) || navPath.includes("portal") || navPath.includes("dashboard")) {
        // Map navigation paths to overlay IDs
        const pathToOverlay = {
          "/staff-portal": "staff_portal",
          "/family-portal": "family_portal",
          "/predictive-alerts": "predictive_alerts",
          "/acuity-trends": "acuity_trends",
          "/patient-acuity": "patient_acuity",
          "/smart-scheduling": "smart_scheduling",
          "/bereavement-support": "bereavement_support"
        };
        const overlayId = pathToOverlay[navPath] || navPath.replace("/", "").replace(/-/g, "_");
        openOverlay(overlayId);
        toast.info(`Opening ${cmd.name}`);
      } else {
        // For other navigations, actually navigate
        navigate(navPath);
        toast.info(`Navigating to ${cmd.name}`);
      }
    } else {
      openOverlay(cmd.id);
    }
    setCommandOpen(false);
    setCommandQuery("");
  }, [openOverlay, navigate]);

  // Search commands
  const searchCommands = useCallback(async (query) => {
    if (!query.trim()) {
      setCommandResults([]);
      return;
    }
    
    try {
      const response = await axios.post(`${API_URL}/api/hos/command-palette/search`, {
        query,
        patient_id: selectedPatient?.id,
        context: {}
      }, { headers: getAuthHeaders() });
      
      setCommandResults(response.data.results || []);
      setContextualSuggestions(response.data.contextual_suggestions || []);
    } catch (err) {
      const q = query.toLowerCase();
      const results = allCommands.filter(cmd => 
        cmd.name.toLowerCase().includes(q) ||
        cmd.keywords?.some(k => k.includes(q)) ||
        cmd.description?.toLowerCase().includes(q)
      );
      setCommandResults(results);
    }
  }, [selectedPatient, allCommands]);

  // Voice dictation
  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: "audio/wav" });
        await transcribeAudio(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorder.start();
      setIsRecording(true);
      toast.info("Recording started - speak now");
      
      const response = await axios.post(`${API_URL}/api/hos/voice/idg-dictation`, {
        patient_id: selectedPatient?.id,
        meeting_id: overlayData?.meeting_id,
        context: "idg_meeting"
      }, { headers: getAuthHeaders() });
      setVoiceSessionId(response.data.session_id);
      
    } catch (err) {
      console.error("Failed to start recording:", err);
      toast.error("Could not access microphone");
    }
  }, [selectedPatient, overlayData]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      toast.info("Processing audio...");
    }
  }, [isRecording]);

  const transcribeAudio = useCallback(async (audioBlob) => {
    try {
      const formData = new FormData();
      formData.append("audio_file", audioBlob, "recording.wav");
      formData.append("context", "idg_meeting");
      formData.append("patient_id", selectedPatient?.id || "demo-patient");
      
      const response = await axios.post(`${API_URL}/api/hos/voice/transcribe`, formData, {
        headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" }
      });
      
      if (response.data.success) {
        setTranscription(prev => prev + (prev ? "\n\n" : "") + response.data.transcription);
        toast.success("Transcription complete");
      }
    } catch (err) {
      setTranscription(prev => prev + (prev ? "\n\n" : "") + "[Demo] Patient is resting comfortably...");
      toast.success("Demo transcription added");
    }
  }, [selectedPatient]);

  // Compliance check
  const checkCompliance = useCallback(async (content) => {
    setNoteContent(content);
    
    if (content.length < 50) {
      setComplianceResult(null);
      return;
    }
    
    try {
      const response = await axios.post(`${API_URL}/api/hos/compliance/check`, {
        patient_id: selectedPatient?.id,
        note_content: content,
        note_type: activeOverlay || "visit_note"
      }, { headers: getAuthHeaders() });
      
      setComplianceResult(response.data);
    } catch (err) {
      const hasVitals = /\d+\/\d+|bp|temp|pulse|spo2/i.test(content);
      const hasPain = /pain/i.test(content);
      const hasPlan = /plan|goal/i.test(content);
      
      setComplianceResult({
        compliant: hasVitals && hasPain && hasPlan,
        grade: hasVitals && hasPain && hasPlan ? "A" : hasPain || hasVitals ? "B" : "C",
        missing_fields: [
          !hasPain && "Pain assessment",
          !hasVitals && "Vital signs",
          !hasPlan && "Plan of care"
        ].filter(Boolean),
        suggestions: !hasVitals ? ["Add objective measurements"] : []
      });
    }
  }, [selectedPatient, activeOverlay]);

  // Switch patient
  const switchPatient = useCallback((patient) => {
    setSelectedPatient(patient);
    fetchPatientContext(patient.id);
    setQuickSwitchOpen(false);
    toast.success(`Switched to ${patient.name}`);
  }, [fetchPatientContext]);

  const value = {
    // Patient state
    selectedPatient,
    setSelectedPatient,
    patientContext,
    patientList,
    timeline,
    loading,
    
    // Overlay state
    activeOverlay,
    overlayData,
    openOverlay,
    closeOverlay,
    
    // Command palette
    commandOpen,
    setCommandOpen,
    commandQuery,
    setCommandQuery,
    commandResults,
    contextualSuggestions,
    allCommands,
    executeCommand,
    searchCommands,
    
    // Patient quick-switch
    quickSwitchOpen,
    setQuickSwitchOpen,
    switchPatient,
    
    // Voice dictation
    isRecording,
    transcription,
    setTranscription,
    startRecording,
    stopRecording,
    
    // Timeline
    timelineFilters,
    setTimelineFilters,
    fetchTimeline,
    
    // Compliance
    complianceResult,
    noteContent,
    checkCompliance,
    
    // Utilities
    getRecommendedTemplate,
    fetchPatientContext,
    
    // Navigation
    navigate,
    
    // Access Control
    userRole,
    isAdmin,
    canAccessFeature: (featureId) => canAccessFeature(userRole, featureId)
  };

  return (
    <HOSContext.Provider value={value}>
      {children}
    </HOSContext.Provider>
  );
}

// Custom hook to use HOS context
export function useHOS() {
  const context = useContext(HOSContext);
  if (!context) {
    throw new Error("useHOS must be used within a HOSProvider");
  }
  return context;
}

// Default commands fallback
function getDefaultCommands(isAdmin = false) {
  const baseCommands = [
    // NOTE: "New Visit Note" redirects to Smart Documentation (duplicate removed)
    { id: "new_visit_note", name: "New Visit Note", description: "Create visit note via Smart Documentation", category: "documentation", keywords: ["note", "visit"], icon: "file-text", shortcut: "Ctrl+N", action: "navigate:/smart-documentation", deprecated: true, redirectTo: "smart_documentation" },
    { id: "smart_documentation", name: "Smart Documentation", description: "AI-powered documentation with suggestions", category: "documentation", keywords: ["smart", "ai", "note", "visit"], icon: "brain", shortcut: "Ctrl+D", action: "navigate:/smart-documentation" },
    { id: "rn_voice_command", name: "RN Command Center", description: "Voice documentation, caseload & alerts for RNs", category: "documentation", keywords: ["voice", "rn", "dictation", "speech", "command", "center"], icon: "stethoscope", shortcut: "Ctrl+Shift+V", action: "navigate:/rn-command-center" },
    { id: "mar", name: "Medication Administration Record", description: "View and manage medication schedules", category: "medication", keywords: ["mar", "medication"], icon: "pill", shortcut: "Ctrl+M" },
    { id: "hope_assessment", name: "HOPE Assessment", description: "Complete or review HOPE assessment", category: "compliance", keywords: ["hope", "assessment"], icon: "clipboard-list", shortcut: "Ctrl+H" },
    { id: "lcd_eligibility", name: "LCD Eligibility Checker", description: "Evaluate hospice eligibility with CMS LCD criteria", category: "compliance", keywords: ["lcd", "eligibility", "hospice", "criteria", "cms"], icon: "clipboard-check", action: "overlay:lcd_eligibility" },
    { id: "ehr_interop", name: "EHR Interoperability", description: "Configure external EHR system connections", category: "admin", keywords: ["ehr", "interoperability", "epic", "cerner", "fhir"], icon: "server", action: "navigate:/ehr-config" },
    { id: "clinical_logic", name: "Clinical Logic Layer", description: "14-Day lookback analysis, POC proposals", category: "clinical", keywords: ["clinical", "logic"], icon: "brain", shortcut: "Ctrl+L" },
    { id: "pain_management", name: "Pain Management", description: "Access pain assessment and medication", category: "clinical", keywords: ["pain"], icon: "heart-pulse" },
    { id: "vital_signs", name: "Vital Signs", description: "Record vital signs", category: "clinical", keywords: ["vital"], icon: "activity" },
    { id: "symptom_assessment", name: "Symptom Assessment", description: "Comprehensive symptom evaluation", category: "clinical", keywords: ["symptom"], icon: "activity" },
    { id: "phone_log", name: "Phone Log", description: "Document phone calls", category: "communication", keywords: ["phone", "call"], icon: "phone" },
    { id: "incident_report", name: "Incident Report", description: "Document an incident", category: "compliance", keywords: ["incident", "fall"], icon: "alert-triangle" },
    { id: "dme_supplies", name: "DME & Supplies", description: "Order equipment and supplies", category: "orders", keywords: ["dme", "equipment"], icon: "package" },
    { id: "visit_template_builder", name: "Visit Template Builder", description: "CMS CoP-compliant templates", category: "documentation", keywords: ["template"], icon: "clipboard-check", action: "overlay:visit_template_builder" },
    { id: "unified_visit", name: "Unified Visit Notes", description: "View consolidated visit notes with CMS compliance", category: "documentation", keywords: ["unified", "visit", "consolidate", "compliance"], icon: "file-text" },
    { id: "plan_of_care", name: "Plan of Care", description: "View and manage care plan", category: "clinical", keywords: ["poc", "care plan"], icon: "clipboard-list" },
    { id: "visit_frequency", name: "Visit Frequency", description: "View and manage visit schedules", category: "clinical", keywords: ["visit", "frequency"], icon: "calendar" },
    { id: "compliance_dashboard", name: "Compliance Dashboard", description: "Supervision tracking, QAPI reporting", category: "compliance", keywords: ["compliance"], icon: "shield" },
    { id: "print_summary", name: "Print Patient Summary", description: "Generate and print comprehensive patient summary", category: "tools", keywords: ["print", "summary", "patient", "report", "pdf"], icon: "printer", shortcut: "Ctrl+P" },
    { id: "idg_discussion", name: "IDG Discussion Note", description: "Document Interdisciplinary Group meeting discussions", category: "communication", keywords: ["idg", "meeting", "interdisciplinary", "team", "discussion"], icon: "users", shortcut: "Ctrl+I" },
    { id: "wound_care", name: "Wound Care", description: "Document wound assessment and care", category: "clinical", keywords: ["wound", "skin", "pressure", "ulcer"], icon: "activity" },
    { id: "consent_forms", name: "Consent Forms", description: "Manage patient consent documents", category: "compliance", keywords: ["consent", "forms", "signature"], icon: "clipboard-check" },
    { id: "care_calendar", name: "Care Calendar", description: "View and manage care schedule", category: "clinical", keywords: ["calendar", "schedule", "appointments"], icon: "calendar" },
    { id: "recertification", name: "Recertification", description: "Manage hospice recertification", category: "compliance", keywords: ["recert", "cti", "certification"], icon: "clipboard-check" },
    { id: "schedule_visit", name: "Schedule Visit", description: "Schedule a new patient visit", category: "clinical", keywords: ["schedule", "visit", "appointment"], icon: "calendar" },
    { id: "symptom_guide", name: "Symptom Management Guide", description: "Reference guide for symptom management", category: "clinical", keywords: ["symptom", "guide", "reference"], icon: "book-open" },
    { id: "export_report", name: "Export Report", description: "Export clinical or operational reports", category: "tools", keywords: ["export", "report", "download", "pdf"], icon: "download" },
    { id: "patient_journey", name: "Patient Journey", description: "View patient timeline and journey", category: "navigation", keywords: ["journey", "timeline", "history"], icon: "git-branch", shortcut: "Ctrl+J" },
    { id: "ai_care_plan", name: "AI Care Plan Recommendations", description: "Get AI-powered care plan suggestions", category: "ai", keywords: ["ai", "care plan", "recommendations", "suggestions"], icon: "brain", action: "overlay:ai_care_plan" },
    { id: "fall_event", name: "Fall Event Documentation", description: "Document fall events and interventions", category: "clinical", keywords: ["fall", "event", "incident", "documentation"], icon: "alert-triangle", action: "overlay:fall_event" },
    { id: "fall_risk_dashboard", name: "Fall Risk Dashboard", description: "Census-wide fall analytics", category: "admin", keywords: ["fall", "risk", "dashboard", "analytics"], icon: "bar-chart", action: "overlay:fall_risk_dashboard", adminOnly: true },
    { id: "clinical_assessments", name: "Clinical Assessments", description: "View falls, wounds, and pain assessments", category: "clinical", keywords: ["assessments", "falls", "wounds", "pain", "clinical"], icon: "clipboard-list", action: "overlay:clinical_assessments" },
    { id: "idg_voice_meeting", name: "IDG Voice Meeting", description: "Start IDG meeting with voice fingerprinting", category: "clinical", keywords: ["idg", "meeting", "voice", "fingerprint", "transcription"], icon: "users", action: "overlay:idg_voice_meeting" },
    { id: "idg_meeting_analytics", name: "IDG Meeting Analytics", description: "Voice meeting trends and participation insights", category: "admin", keywords: ["idg", "analytics", "meetings", "trends", "participation", "voice"], icon: "bar-chart-3", action: "overlay:idg_meeting_analytics", adminOnly: true },
    { id: "idg_insights_email", name: "IDG Insights Email Settings", description: "Configure automated weekly meeting reports", category: "admin", keywords: ["idg", "email", "insights", "report", "weekly", "automated"], icon: "mail", action: "overlay:idg_insights_email", adminOnly: true },
    { id: "voice_command_help", name: "Voice Commands Help", description: "Learn voice commands and shortcuts for hands-free navigation", category: "tools", keywords: ["voice", "speech", "microphone", "help", "commands", "shortcuts", "hands-free"], icon: "mic", action: "overlay:voice_help" },
    // Disabled features - Coming Soon
    { id: "idg_review", name: "IDG Review Mode", description: "Coming Soon - Temporarily disabled", category: "clinical", keywords: ["idg", "review", "mode"], icon: "users", disabled: true, comingSoon: true },
    { id: "clinical_ai_agent", name: "Clinical AI Agent", description: "Coming Soon - Being enhanced for accuracy", category: "clinical", keywords: ["ai", "agent"], icon: "brain", disabled: true, comingSoon: true }
  ];
  
  // Admin-only commands
  const adminCommands = [
    { id: "reports", name: "Reports Dashboard", description: "View clinical and operational reports", category: "admin", keywords: ["report", "analytics"], icon: "chart-bar", adminOnly: true },
    { id: "user_management", name: "User Management", description: "Manage staff accounts and permissions", category: "admin", keywords: ["users", "staff"], icon: "users-cog", adminOnly: true },
    { id: "audit_logs", name: "Audit Logs", description: "View system audit trail", category: "admin", keywords: ["audit", "log"], icon: "file-search", adminOnly: true },
    { id: "visit_sync_admin", name: "Visit Sync Admin", description: "Configure CMS compliance rules and visit synchronization", category: "admin", keywords: ["visit", "sync", "compliance", "cms", "rules"], icon: "shield", adminOnly: true },
    { id: "rcm_management", name: "Revenue Cycle Management", description: "Billing, claims, and revenue dashboard", category: "admin", keywords: ["rcm", "billing", "revenue", "claims"], icon: "dollar-sign", adminOnly: true, action: "overlay:rcm_management" },
    { id: "admin_settings", name: "Admin Settings", description: "System configuration and settings", category: "admin", keywords: ["settings", "config", "admin"], icon: "settings", adminOnly: true, action: "navigate:/admin-settings" },
    { id: "staff_scheduling", name: "Staff Scheduling", description: "Manage staff schedules and assignments", category: "admin", keywords: ["staff", "schedule", "assignments"], icon: "calendar", adminOnly: true, action: "overlay:staff_scheduling" },
    { id: "predictive_alerts", name: "Predictive Escalation Alerts", description: "AI-powered patient decline predictions", category: "admin", keywords: ["predictive", "alerts", "escalation", "ai"], icon: "alert-triangle", adminOnly: true, action: "navigate:/predictive-alerts" },
    { id: "acuity_trends", name: "Patient Acuity Trends", description: "View patient acuity trends dashboard", category: "admin", keywords: ["acuity", "trends", "dashboard"], icon: "trending-up", adminOnly: true, action: "overlay:acuity_trends" }
  ];
  
  // Return all commands for admin, filtered for staff
  if (isAdmin) {
    return [...baseCommands, ...adminCommands];
  }
  
  return baseCommands;
}

export default HOSContext;
